// ── Canvas layout ─────────────────────────────────────────────────────────────
const PRICE_Y_BOTTOM = 0.85; // canvas fraction where min-price sits
const PRICE_Y_TOP = 0.15; // canvas fraction where max-price sits

// ── Background color ──────────────────────────────────────────────────────────
// Shared between p.draw() and palette generation — keep in sync.
const BG_R = 10;
const BG_G = 12;
const BG_B = 30;

// ── Background filler stars ───────────────────────────────────────────────────
const BG_STAR_DENSITY = 120; // px² per background star — lower = denser field
const BG_STAR_SIZE_MIN = 0.3; // px core radius
const BG_STAR_SIZE_MAX = 4.5;
const BG_STAR_ALPHA_MIN = 0.08; // peak opacity range (0–1)
const BG_STAR_ALPHA_MAX = 0.92;
const BG_STAR_GLOW_MIN = 1.8; // glow radius = core * random(GLOW_MIN, GLOW_MAX)
const BG_STAR_GLOW_MAX = 3.8;
const BG_STAR_LARGE_RARITY = 2.8; // power-law exponent applied to size + alpha; higher = rarer large/bright stars (1 = uniform)

// ── Data stars ────────────────────────────────────────────────────────────────
const STAR_CORE_MIN = 1.5; // px radius at zero volume
const STAR_CORE_MAX = 5.5; // px radius at max volume
const STAR_GLOW_FACTOR = 2.5; // glow radius = core * (GLOW_FACTOR + vol * GLOW_VOL_FACTOR)
const STAR_GLOW_VOL = 1.5; // extra glow multiplier scaled by volume
const STAR_SAT_MIN = 0.65; // HSL saturation range (0–1)
const STAR_SAT_MAX = 0.95;
const STAR_LIT_MIN = 0.6; // HSL lightness range (0–1)
const STAR_LIT_MAX = 0.82;

// ── Connecting lines ──────────────────────────────────────────────────────────
const LINE_ALPHA_SPARSE = 0.55; // opacity for sparse datasets (< LINE_DENSE_THRESHOLD candles)
const LINE_ALPHA_DENSE = 0.7; // opacity for dense datasets
const LINE_WIDTH_SPARSE = 1.5; // px stroke width for sparse datasets
const LINE_WIDTH_DENSE = 0.9; // px stroke width for dense datasets
const LINE_DENSE_THRESHOLD = 150; // candle count above which dense alpha kicks in
const LINE_THIN_THRESHOLD = 300; // candle count above which thin lines kick in

// ── Cosmic dust / nebulae ─────────────────────────────────────────────────
const NEBULA_COUNT_MIN = 2; // minimum number of nebula patches
const NEBULA_COUNT_MAX = 5; // maximum number of nebula patches
const NEBULA_PUFF_MIN = 5; // min radial gradient "puffs" per patch
const NEBULA_PUFF_MAX = 10; // max radial gradient "puffs" per patch
const NEBULA_RADIUS_MIN = 0.08; // puff radius as fraction of canvas half-diagonal
const NEBULA_RADIUS_MAX = 0.22; // puff radius as fraction of canvas half-diagonal
const NEBULA_SPREAD = 0.18; // how far puff centers scatter from patch center (half-diag fraction)
const NEBULA_OPACITY_MAX = 0.4; // max alpha for any single nebula puff

// ── Planet ────────────────────────────────────────────────────────────────────
const PLANET_CHANCE = 0.6; // probability any planets spawn each generation
const PLANET_COUNT_MIN = 1; // min planets when spawned
const PLANET_COUNT_MAX = 3; // max planets when spawned
const PLANET_RADIUS_MIN = 50; // px — sphere radius (not including rings)
const PLANET_RADIUS_MAX = 150; // px
const PLANET_RING_CHANCE = 0.4; // probability of rings
const PLANET_MARGIN = 15; // px clearance between planet edge and chart zone

// ── Moons ─────────────────────────────────────────────────────────────────────
const MOON_CHANCE = 0.6; // probability a planet has moons
const MOON_COUNT_MIN = 1; // moons per planet when present
const MOON_COUNT_MAX = 3;
const MOON_RADIUS_MIN = 0.12; // fraction of parent planet baseRadius
const MOON_RADIUS_MAX = 0.28;
const MOON_ORBIT_MIN = 1.3; // orbit radius as multiple of planet totalR
const MOON_ORBIT_MAX = 1.75;

// ── Vignette ──────────────────────────────────────────────────────────────────
// Radii are fractions of the canvas half-diagonal so the gradient scales with
// aspect ratio. OUTER > 1.0 pushes the fully-dark ring beyond the corners,
// keeping the left/right edges (where end stars sit) inside the soft falloff.
const VIGNETTE_INNER = 0.2; // inner clear radius as fraction of half-diagonal
const VIGNETTE_OUTER = 1.1; // outer dark radius as fraction of half-diagonal
const VIGNETTE_OPACITY = 0.55; // max vignette darkness (0–1)

const hslToRgb = (h, s, l) => {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0,
        g = 0,
        b = 0;
    if (h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (h < 300) {
        r = x;
        g = 0;
        b = c;
    } else {
        r = c;
        g = 0;
        b = x;
    }
    return [
        Math.round((r + m) * 255),
        Math.round((g + m) * 255),
        Math.round((b + m) * 255),
    ];
};

const toHex = (r, g, b) =>
    `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;

// Blend (r,g,b) at `alpha` over the background — matches the actual glow stops
// rendered in draw so the pixel-art palette has entries for those in-between colors.
const blendOverBg = (r, g, b, alpha) =>
    toHex(
        Math.round(r * alpha + BG_R * (1 - alpha)),
        Math.round(g * alpha + BG_G * (1 - alpha)),
        Math.round(b * alpha + BG_B * (1 - alpha))
    );

export const constellationMap = (p, chartData) => {
    let planetsData = [];
    let nebulaData = [];
    let bgStarData = [];
    let lineData = [];
    let lineColor = null;
    let starData = [];

    p.setup = () => {
        p.createCanvas(p.windowWidth, p.windowHeight);

        const W = p.width;
        const H = p.height;

        const vals = chartData.values;
        const N = vals.length;
        const closes = vals.map((v) => v.close);
        const volumes = vals.map((v) =>
            v.volume != null ? v.volume : (v.high - v.low) / (v.close || 1)
        );

        const lo = Math.min(...closes);
        const hi = Math.max(...closes);
        const priceRange = hi - lo || 1;
        const maxVol = Math.max(...volumes) || 1;
        const minVol = Math.min(...volumes);
        const volRange = maxVol - minVol || 1;

        const candleX = (i) => (N > 1 ? (i / (N - 1)) * W : W / 2);
        const priceToY = (price) => {
            const ph = (price - lo) / priceRange;
            return H * (PRICE_Y_BOTTOM - (PRICE_Y_BOTTOM - PRICE_Y_TOP) * ph);
        };
        const normVol = (v) => (v - minVol) / volRange;

        // Cosmic dust / nebulae — wispy radial-gradient puffs behind everything
        const halfDiagSetup = Math.hypot(W / 2, H / 2);
        const nebulaCount = Math.round(
            p.random(NEBULA_COUNT_MIN, NEBULA_COUNT_MAX)
        );
        // Full nebula hue palette — real nebulae span reds, golds, greens, blues, purples
        const nebulaHueBands = [
            [  0,  18], // deep red (hydrogen-alpha)
            [ 18,  45], // red-orange / warm emission
            [ 45,  65], // gold / amber
            [150, 185], // teal-green (oxygen-III)
            [195, 230], // blue-cyan
            [250, 290], // blue-purple
            [295, 325], // pink-magenta
            [325, 355], // crimson-rose
        ];
        for (let n = 0; n < nebulaCount; n += 1) {
            const cx = p.random(W * 0.1, W * 0.9);
            const cy = p.random(H * 0.1, H * 0.9);
            const band =
                nebulaHueBands[Math.floor(p.random(nebulaHueBands.length))];
            const hue = p.random(band[0], band[1]);
            const [nr, ng, nb] = hslToRgb(
                hue,
                p.random(0.55, 0.85),
                p.random(0.25, 0.45)
            );
            const puffCount = Math.round(
                p.random(NEBULA_PUFF_MIN, NEBULA_PUFF_MAX)
            );
            const puffs = [];
            for (let k = 0; k < puffCount; k += 1) {
                puffs.push({
                    x     : cx + p.random(-1, 1) * halfDiagSetup * NEBULA_SPREAD,
                    y     : cy + p.random(-1, 1) * halfDiagSetup * NEBULA_SPREAD,
                    radius: halfDiagSetup * p.random(NEBULA_RADIUS_MIN, NEBULA_RADIUS_MAX),
                    alpha : p.random(NEBULA_OPACITY_MAX * 0.4, NEBULA_OPACITY_MAX),
                    angle : p.random(Math.PI * 2),
                    ecc   : p.random(0.32, 0.72),  // minor/major ratio — lower = more elongated
                });
            }

            // Tendrils — wispy elongated arms radiating from the nebula centre
            const tendrilCount = Math.floor(p.random(5, 10));
            const tendrils = Array.from({ length: tendrilCount }, () => ({
                angle    : p.random(Math.PI * 2),
                length   : halfDiagSetup * p.random(0.13, 0.44),
                widthFrac: p.random(0.06, 0.16),
                alpha    : p.random(NEBULA_OPACITY_MAX * 0.22, NEBULA_OPACITY_MAX * 0.55),
                steps    : Math.floor(p.random(5, 9)),
            }));

            nebulaData.push({ r: nr, g: ng, b: nb, cx, cy, puffs, tendrils });
        }

        // Background filler stars — random hue, slightly desaturated so they
        // read as background rather than competing with the data stars
        const bgCount = Math.floor((W * H) / BG_STAR_DENSITY);
        for (let i = 0; i < bgCount; i += 1) {
            const [r, g, b] = hslToRgb(
                p.random(360),
                p.random(0.2, 0.55),
                p.random(0.55, 0.85)
            );
            const size =
                BG_STAR_SIZE_MIN +
                (BG_STAR_SIZE_MAX - BG_STAR_SIZE_MIN) *
                    Math.pow(p.random(), BG_STAR_LARGE_RARITY);
            const alpha =
                BG_STAR_ALPHA_MIN +
                (BG_STAR_ALPHA_MAX - BG_STAR_ALPHA_MIN) *
                    Math.pow(p.random(), BG_STAR_LARGE_RARITY);
            bgStarData.push({
                x: p.random(W),
                y: p.random(H),
                glowR: size * p.random(BG_STAR_GLOW_MIN, BG_STAR_GLOW_MAX),
                alpha,
                r,
                g,
                b,
            });
        }

        // Data stars — each gets a random hue, high saturation, fixed brightness range
        for (let i = 0; i < N; i += 1) {
            const x = candleX(i);
            const y = priceToY(closes[i]);
            const nv = normVol(volumes[i]);
            const coreR = STAR_CORE_MIN + nv * (STAR_CORE_MAX - STAR_CORE_MIN);
            const glowR = coreR * (STAR_GLOW_FACTOR + nv * STAR_GLOW_VOL);

            const [r, g, b] = hslToRgb(
                p.random(360),
                p.random(STAR_SAT_MIN, STAR_SAT_MAX),
                p.random(STAR_LIT_MIN, STAR_LIT_MAX)
            );

            starData.push({ x, y, glowR, r, g, b });
        }

        // Connecting lines — single randomised color per generation so the line
        // reads as a navigator rather than competing with individual star colors.
        const [lr, lg, lb] = hslToRgb(
            p.random(360),
            p.random(0.3, 0.65),
            p.random(0.55, 0.75)
        );
        lineColor = { r: lr, g: lg, b: lb };
        const lineAlpha =
            N > LINE_DENSE_THRESHOLD ? LINE_ALPHA_DENSE : LINE_ALPHA_SPARSE;
        const lineWidth =
            N > LINE_THIN_THRESHOLD ? LINE_WIDTH_DENSE : LINE_WIDTH_SPARSE;
        for (let i = 0; i < N - 1; i += 1) {
            const a = starData[i];
            const b = starData[i + 1];
            lineData.push({
                x1: a.x,
                y1: a.y,
                x2: b.x,
                y2: b.y,
                alpha: lineAlpha,
                width: lineWidth,
            });
        }

        // Planets — 1-3 in the top/bottom strips, each with optional moons
        if (p.random() < PLANET_CHANCE) {
            const count = Math.floor(
                p.random(PLANET_COUNT_MIN, PLANET_COUNT_MAX + 1)
            );
            for (let pi = 0; pi < count; pi += 1) {
                // Size is unconstrained by strip height — placement constrains the
                // sphere *edge* instead of the center, so large planets appear as
                // dramatic arcs at the canvas top/bottom.
                if (PLANET_RADIUS_MAX < PLANET_RADIUS_MIN) continue;

                let hasRings = p.random() < PLANET_RING_CHANCE;
                let ringInnerFactor = hasRings ? p.random(1.25, 2.0) : 0;
                let ringExtFactor = hasRings ? p.random(0.3, 1.2) : 0;
                let sizeMultiplier = hasRings
                    ? ringInnerFactor + ringExtFactor
                    : 1;

                const baseRadius = p.random(
                    PLANET_RADIUS_MIN,
                    PLANET_RADIUS_MAX
                );
                const ringInnerR = hasRings ? baseRadius * ringInnerFactor : 0;
                const ringOuterR = hasRings ? baseRadius * sizeMultiplier : 0;
                const ringTilt = hasRings ? p.random(0.2, 0.45) : 0;
                const totalR = hasRings ? ringOuterR : baseRadius;

                // Strict on-screen bounds: cx/cy ± totalR must stay within [0,W] and [0,H]
                // and outside the chart zone.
                const cxMin = totalR;
                const cxMax = W - totalR;
                if (cxMin >= cxMax) continue;

                // Try both strips, alternating per attempt, to maximise placement chances.
                let cx = 0,
                    cy = 0,
                    placed = false;
                for (let attempt = 0; attempt < 8 && !placed; attempt += 1) {
                    const useTop = attempt % 2 === 0;
                    // Full sphere edge must stay clear of the chart zone.
                    const cyMin = useTop
                        ? PLANET_MARGIN
                        : H * PRICE_Y_BOTTOM + PLANET_MARGIN + totalR;
                    const cyMax = useTop
                        ? H * PRICE_Y_TOP - PLANET_MARGIN - totalR
                        : H - PLANET_MARGIN;
                    if (cyMin > cyMax) continue;

                    const candCx = p.random(cxMin, cxMax);
                    const candCy = p.random(cyMin, cyMax);
                    const overlaps = planetsData.some((existing) => {
                        const existR = existing.hasRings
                            ? existing.ringOuterR
                            : existing.baseRadius;
                        return (
                            Math.hypot(
                                candCx - existing.cx,
                                candCy - existing.cy
                            ) <
                            totalR + existR + 20
                        );
                    });
                    if (!overlaps) {
                        cx = candCx;
                        cy = candCy;
                        placed = true;
                    }
                }
                if (!placed) continue;

                const hue = p.random(360);
                const sat = p.random(0.4, 0.75);
                const [pr, pg, pb] = hslToRgb(hue, sat, p.random(0.45, 0.65));
                const [pr2, pg2, pb2] = hslToRgb(
                    (hue + p.random(20, 50)) % 360,
                    sat * 0.8,
                    p.random(0.2, 0.4)
                );
                const [ar, ag, ab] = hslToRgb(
                    (hue + p.random(-25, 25) + 360) % 360,
                    sat * 0.7,
                    p.random(0.6, 0.8)
                );

                let ringR = 0,
                    ringG = 0,
                    ringB = 0,
                    ringAlpha = 0;
                if (hasRings) {
                    const [rr, rg, rb] = hslToRgb(
                        (hue + p.random(30, 80)) % 360,
                        p.random(0.3, 0.65),
                        p.random(0.55, 0.75)
                    );
                    ringR = rr;
                    ringG = rg;
                    ringB = rb;
                    ringAlpha = p.random(0.5, 0.85);
                }

                const bandCount = Math.floor(p.random(2, 6));
                const bands = Array.from({ length: bandCount }, () => {
                    const [br, bg, bb] = hslToRgb(
                        (hue + p.random(-50, 50) + 360) % 360,
                        p.random(0.3, 0.7),
                        p.random(0.2, 0.5)
                    );
                    return {
                        y: p.random(-baseRadius * 0.9, baseRadius * 0.9),
                        width: p.random(baseRadius * 0.06, baseRadius * 0.22),
                        r: br,
                        g: bg,
                        b: bb,
                        alpha: p.random(0.1, 0.4),
                        cloudSeed: p.random(1, 99),
                    };
                });

                // Moons — small spheres at a random orbital angle around this planet
                const moons = [];
                if (p.random() < MOON_CHANCE) {
                    const moonCount = Math.floor(
                        p.random(MOON_COUNT_MIN, MOON_COUNT_MAX + 1)
                    );
                    for (let mi = 0; mi < moonCount; mi += 1) {
                        const moonRadius =
                            baseRadius *
                            p.random(MOON_RADIUS_MIN, MOON_RADIUS_MAX);
                        const orbitRadius =
                            totalR * p.random(MOON_ORBIT_MIN, MOON_ORBIT_MAX) +
                            moonRadius;
                        const angle = p.random(Math.PI * 2);
                        const moonHue = (hue + p.random(-40, 40) + 360) % 360;
                        const moonSat = p.random(0.15, 0.45);
                        const moonLit = p.random(0.35, 0.6);
                        const [mr, mg, mb] = hslToRgb(
                            moonHue,
                            moonSat,
                            moonLit
                        );
                        const [mr2, mg2, mb2] = hslToRgb(
                            moonHue,
                            moonSat * 0.6,
                            moonLit * 0.35
                        );
                        moons.push({
                            x: cx + Math.cos(angle) * orbitRadius,
                            y: cy + Math.sin(angle) * orbitRadius,
                            radius: moonRadius,
                            r: mr,
                            g: mg,
                            b: mb,
                            r2: mr2,
                            g2: mg2,
                            b2: mb2,
                        });
                    }
                }

                planetsData.push({
                    cx,
                    cy,
                    baseRadius,
                    r: pr,
                    g: pg,
                    b: pb,
                    r2: pr2,
                    g2: pg2,
                    b2: pb2,
                    ar,
                    ag,
                    ab,
                    bands,
                    moons,
                    hasRings,
                    ringInnerR,
                    ringOuterR,
                    ringTilt,
                    ringR,
                    ringG,
                    ringB,
                    ringAlpha,
                });
            }
        }

        // Palette — each star contributes three entries (full + two glow-fade stops
        // that match the 0.55 and 0.25 alpha stops rendered in draw) so the
        // pixel-art quantizer doesn't collapse all glow falloffs to the background.
        const seen = new Set();
        p._generatedPalette = [
            toHex(BG_R, BG_G, BG_B),
            '#0f1228',
            '#c8cdff',
            ...starData.flatMap(({ r, g, b }) => [
                toHex(r, g, b),
                blendOverBg(r, g, b, 0.55),
                blendOverBg(r, g, b, 0.25),
            ]),
            ...(lineColor
                ? [
                      toHex(lineColor.r, lineColor.g, lineColor.b),
                      blendOverBg(lineColor.r, lineColor.g, lineColor.b, 0.5),
                  ]
                : []),
            ...nebulaData.flatMap(({ r, g, b }) => [
                toHex(r, g, b),
                blendOverBg(r, g, b, 0.45),
            ]),
            ...planetsData.flatMap(
                ({
                    r,
                    g,
                    b,
                    r2,
                    g2,
                    b2,
                    ar,
                    ag,
                    ab,
                    hasRings,
                    ringR,
                    ringG,
                    ringB,
                    moons,
                }) => [
                    toHex(r, g, b),
                    toHex(r2, g2, b2),
                    blendOverBg(r, g, b, 0.6),
                    toHex(ar, ag, ab),
                    blendOverBg(ar, ag, ab, 0.22),
                    blendOverBg(ar, ag, ab, 0.55),
                    ...(hasRings ? [toHex(ringR, ringG, ringB)] : []),
                    ...moons.flatMap(
                        ({
                            r: mr,
                            g: mg,
                            b: mb,
                            r2: mr2,
                            g2: mg2,
                            b2: mb2,
                        }) => [toHex(mr, mg, mb), toHex(mr2, mg2, mb2)]
                    ),
                ]
            ),
        ].filter((hex) => !seen.has(hex) && seen.add(hex));

        p.noLoop();
    };

    p.draw = () => {
        const W = p.width;
        const H = p.height;
        const ctx = p.drawingContext;

        p.background(BG_R, BG_G, BG_B);

        // Cosmic dust — tendrils then elliptical puffs, drawn first behind everything
        nebulaData.forEach(({ r, g, b, cx: ncx, cy: ncy, puffs, tendrils }) => {
            tendrils.forEach(({ angle, length, widthFrac, alpha, steps }) => {
                const stepLen = length / steps;
                for (let s = 0; s < steps; s += 1) {
                    const t = (s + 0.5) / steps;
                    const fd = Math.pow(1 - t, 0.7);
                    const tx = ncx + Math.cos(angle) * length * t;
                    const ty = ncy + Math.sin(angle) * length * t;
                    const rAlong = stepLen * 1.1;
                    const rAcross = Math.max(4, length * widthFrac * (1 - t * 0.5));
                    ctx.save();
                    ctx.translate(tx, ty);
                    ctx.rotate(angle);
                    ctx.scale(rAlong, rAcross);
                    const gd = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
                    gd.addColorStop(0, `rgba(${r},${g},${b},${(alpha * fd).toFixed(3)})`);
                    gd.addColorStop(0.55, `rgba(${r},${g},${b},${(alpha * fd * 0.35).toFixed(3)})`);
                    gd.addColorStop(1, `rgba(${r},${g},${b},0)`);
                    ctx.fillStyle = gd;
                    ctx.beginPath();
                    ctx.arc(0, 0, 1, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.restore();
                }
            });
            puffs.forEach(({ x, y, radius, alpha, angle, ecc }) => {
                ctx.save();
                ctx.translate(x, y);
                ctx.rotate(angle);
                ctx.scale(1, ecc);
                const gd = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
                gd.addColorStop(0, `rgba(${r},${g},${b},${alpha.toFixed(3)})`);
                gd.addColorStop(0.5, `rgba(${r},${g},${b},${(alpha * 0.35).toFixed(3)})`);
                gd.addColorStop(1, `rgba(${r},${g},${b},0)`);
                ctx.fillStyle = gd;
                ctx.beginPath();
                ctx.arc(0, 0, radius, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
            });
        });

        // Background filler stars — radial gradient gives larger ones a proper glow
        bgStarData.forEach(({ x, y, glowR, alpha, r, g, b }) => {
            const grad = ctx.createRadialGradient(x, y, 0, x, y, glowR);
            grad.addColorStop(0, `rgba(${r},${g},${b},${alpha.toFixed(2)})`);
            grad.addColorStop(
                0.3,
                `rgba(${r},${g},${b},${(alpha * 0.4).toFixed(2)})`
            );
            grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
            ctx.fillStyle = grad;
            ctx.beginPath();
            ctx.arc(x, y, glowR, 0, Math.PI * 2);
            ctx.fill();
        });

        // Planets and their moons
        const drawMoon = (mx, my, moonR, mr, mg, mb, mr2, mg2, mb2) => {
            const mhlX = mx - moonR * 0.3;
            const mhlY = my - moonR * 0.3;
            const moonGrad = ctx.createRadialGradient(
                mhlX,
                mhlY,
                moonR * 0.05,
                mx,
                my,
                moonR
            );
            moonGrad.addColorStop(0, 'rgba(255,255,255,0.20)');
            moonGrad.addColorStop(0.3, `rgba(${mr},${mg},${mb},1)`);
            moonGrad.addColorStop(1, `rgba(${mr2},${mg2},${mb2},1)`);
            ctx.fillStyle = moonGrad;
            ctx.beginPath();
            ctx.arc(mx, my, moonR, 0, Math.PI * 2);
            ctx.fill();
        };

        planetsData.forEach(
            ({
                cx,
                cy,
                baseRadius,
                r,
                g,
                b,
                r2,
                g2,
                b2,
                ar,
                ag,
                ab,
                bands,
                moons,
                hasRings,
                ringInnerR,
                ringOuterR,
                ringTilt,
                ringR,
                ringG,
                ringB,
                ringAlpha,
            }) => {
                const ringThick = ringOuterR - ringInnerR;

                ctx.save();

                // Moons behind the planet (screen-space y < planet center = farther away)
                moons
                    .filter((m) => m.y < cy)
                    .forEach(
                        ({
                            x: mx,
                            y: my,
                            radius: moonR,
                            r: mr,
                            g: mg,
                            b: mb,
                            r2: mr2,
                            g2: mg2,
                            b2: mb2,
                        }) => drawMoon(mx, my, moonR, mr, mg, mb, mr2, mg2, mb2)
                    );

                // Back half of ring — above planet center, slightly dimmer
                if (hasRings) {
                    ctx.save();
                    ctx.beginPath();
                    ctx.rect(0, 0, W, cy);
                    ctx.clip();
                    const ringSteps = 18;
                    const strokeW = (ringThick / ringSteps) * 2.4;
                    for (let s = 0; s < ringSteps; s++) {
                        const t = (s + 0.5) / ringSteps;
                        const r = ringInnerR + ringThick * t;
                        const bell = Math.sin(t * Math.PI);
                        ctx.strokeStyle = `rgba(${ringR},${ringG},${ringB},${(ringAlpha * 0.65 * bell).toFixed(3)})`;
                        ctx.lineWidth = strokeW;
                        ctx.beginPath();
                        ctx.ellipse(cx, cy, r, r * ringTilt, 0, 0, Math.PI * 2);
                        ctx.stroke();
                    }
                    ctx.restore();
                }

                // Atmosphere glow
                const atmosGrad = ctx.createRadialGradient(
                    cx,
                    cy,
                    baseRadius * 0.85,
                    cx,
                    cy,
                    baseRadius * 1.4
                );
                atmosGrad.addColorStop(0, `rgba(${ar},${ag},${ab},0.18)`);
                atmosGrad.addColorStop(1, `rgba(${ar},${ag},${ab},0)`);
                ctx.fillStyle = atmosGrad;
                ctx.beginPath();
                ctx.arc(cx, cy, baseRadius * 1.4, 0, Math.PI * 2);
                ctx.fill();

                // Planet sphere — off-center radial gradient simulates directional lighting
                const hlX = cx - baseRadius * 0.3;
                const hlY = cy - baseRadius * 0.3;
                const sphereGrad = ctx.createRadialGradient(
                    hlX,
                    hlY,
                    baseRadius * 0.05,
                    cx,
                    cy,
                    baseRadius
                );
                sphereGrad.addColorStop(0, 'rgba(255,255,255,0.32)');
                sphereGrad.addColorStop(0.28, `rgba(${r},${g},${b},1)`);
                sphereGrad.addColorStop(1, `rgba(${r2},${g2},${b2},1)`);
                ctx.fillStyle = sphereGrad;
                ctx.beginPath();
                ctx.arc(cx, cy, baseRadius, 0, Math.PI * 2);
                ctx.fill();

                // Cloud bands + rim lighting — all clipped to sphere
                ctx.save();
                ctx.beginPath();
                ctx.arc(cx, cy, baseRadius, 0, Math.PI * 2);
                ctx.clip();
                bands.forEach(({ y: by, width, r: br, g: bg, b: bb, alpha, cloudSeed }) => {
                    const bY = cy + by;
                    const bx0 = cx - baseRadius;
                    const bx1 = cx + baseRadius;
                    const wAmp = width * 0.45;
                    const wFreq = 2.5 + (cloudSeed % 3.0);
                    const phase = (cloudSeed / 99) * Math.PI * 4;
                    ctx.fillStyle = `rgba(${br},${bg},${bb},${alpha})`;
                    ctx.beginPath();
                    ctx.moveTo(bx0, bY - width / 2 + Math.sin(phase) * wAmp);
                    for (let px = bx0 + 3; px <= bx1; px += 3) {
                        const t = (px - bx0) / (bx1 - bx0);
                        ctx.lineTo(px, bY - width / 2 + Math.sin(t * Math.PI * wFreq + phase) * wAmp);
                    }
                    for (let px = bx1; px >= bx0; px -= 3) {
                        const t = (px - bx0) / (bx1 - bx0);
                        ctx.lineTo(px, bY + width / 2 + Math.sin(t * Math.PI * wFreq + phase + 1.5) * wAmp);
                    }
                    ctx.closePath();
                    ctx.fill();
                });
                // Rim lighting — atmosphere brightens the dark limb opposite the light source
                const rimGrad = ctx.createRadialGradient(hlX, hlY, baseRadius * 0.72, hlX, hlY, baseRadius * 2.10);
                rimGrad.addColorStop(0, 'rgba(0,0,0,0)');
                rimGrad.addColorStop(0.30, 'rgba(0,0,0,0)');
                rimGrad.addColorStop(0.62, `rgba(${ar},${ag},${ab},0.22)`);
                rimGrad.addColorStop(1, `rgba(${ar},${ag},${ab},0.55)`);
                ctx.fillStyle = rimGrad;
                ctx.fillRect(cx - baseRadius, cy - baseRadius, baseRadius * 2, baseRadius * 2);
                ctx.restore();

                // Front half of ring — below planet center, full brightness
                if (hasRings) {
                    ctx.save();
                    ctx.beginPath();
                    ctx.rect(0, cy, W, H - cy);
                    ctx.clip();
                    const ringSteps = 18;
                    const strokeW = (ringThick / ringSteps) * 2.4;
                    for (let s = 0; s < ringSteps; s++) {
                        const t = (s + 0.5) / ringSteps;
                        const r = ringInnerR + ringThick * t;
                        const bell = Math.sin(t * Math.PI);
                        ctx.strokeStyle = `rgba(${ringR},${ringG},${ringB},${(ringAlpha * bell).toFixed(3)})`;
                        ctx.lineWidth = strokeW;
                        ctx.beginPath();
                        ctx.ellipse(cx, cy, r, r * ringTilt, 0, 0, Math.PI * 2);
                        ctx.stroke();
                    }
                    ctx.restore();
                }

                // Moons in front of the planet (screen-space y >= planet center = closer)
                moons
                    .filter((m) => m.y >= cy)
                    .forEach(
                        ({
                            x: mx,
                            y: my,
                            radius: moonR,
                            r: mr,
                            g: mg,
                            b: mb,
                            r2: mr2,
                            g2: mg2,
                            b2: mb2,
                        }) => drawMoon(mx, my, moonR, mr, mg, mb, mr2, mg2, mb2)
                    );

                ctx.restore();
            }
        );

        // Connecting lines — uniform color across all segments
        if (lineColor) {
            ctx.strokeStyle = `rgba(${lineColor.r},${lineColor.g},${lineColor.b},${lineData[0]?.alpha ?? LINE_ALPHA_SPARSE})`;
            ctx.lineWidth = lineData[0]?.width ?? LINE_WIDTH_SPARSE;
            ctx.beginPath();
            lineData.forEach(({ x1, y1, x2, y2 }) => {
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
            });
            ctx.stroke();
        }

        // Data stars — skip any whose center falls inside a planet's solid sphere
        starData.forEach(({ x, y, glowR, r, g, b }) => {
            const insidePlanet = planetsData.some(
                ({ cx, cy, baseRadius }) =>
                    Math.hypot(x - cx, y - cy) < baseRadius
            );
            if (insidePlanet) return;
            const grad = ctx.createRadialGradient(x, y, 0, x, y, glowR);
            grad.addColorStop(0, 'rgba(255,255,255,1)');
            grad.addColorStop(0.12, `rgba(${r},${g},${b},0.95)`);
            grad.addColorStop(0.45, `rgba(${r},${g},${b},0.35)`);
            grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
            ctx.fillStyle = grad;
            ctx.beginPath();
            ctx.arc(x, y, glowR, 0, Math.PI * 2);
            ctx.fill();
        });

        // Vignette — half-diagonal reference keeps falloff consistent across aspect ratios
        const halfDiag = Math.hypot(W / 2, H / 2);
        const vg = ctx.createRadialGradient(
            W / 2,
            H / 2,
            halfDiag * VIGNETTE_INNER,
            W / 2,
            H / 2,
            halfDiag * VIGNETTE_OUTER
        );
        vg.addColorStop(0, 'rgba(0,0,0,0)');
        vg.addColorStop(1, `rgba(0,0,8,${VIGNETTE_OPACITY})`);
        ctx.fillStyle = vg;
        ctx.fillRect(0, 0, W, H);
    };
};
